export const BRAND_FONT_IDS = [
  "inter",
  "manrope",
  "space-grotesk",
  "dm-sans",
  "plus-jakarta-sans",
  "work-sans",
  "arial",
  "georgia",
  "verdana",
  "times-new-roman",
  "courier-new",
] as const;

export type BrandFontId = (typeof BRAND_FONT_IDS)[number];

export interface BrandTypographySelection {
  headingFontId: BrandFontId;
  bodyFontId: BrandFontId;
  rationale?: string;
}

export interface BrandTypographyOption {
  id: BrandFontId;
  label: string;
  category: string;
  traits: string;
  cssFamily: string;
}

export const DEFAULT_HEADING_FONT_ID: BrandFontId = "inter";
export const DEFAULT_BODY_FONT_ID: BrandFontId = "inter";

export const BRAND_TYPOGRAPHY_CATALOG: BrandTypographyOption[] = [
  {
    id: "inter",
    label: "Inter",
    category: "Neo-grotesk sans",
    traits: "neutral, modern, UI-friendly, highly legible",
    cssFamily: "Inter, system-ui, sans-serif",
  },
  {
    id: "manrope",
    label: "Manrope",
    category: "Geometric sans",
    traits: "clean, confident, premium, contemporary",
    cssFamily: "Manrope, system-ui, sans-serif",
  },
  {
    id: "space-grotesk",
    label: "Space Grotesk",
    category: "Grotesk sans",
    traits: "tech-forward, expressive, editorial, modern",
    cssFamily: "'Space Grotesk', system-ui, sans-serif",
  },
  {
    id: "dm-sans",
    label: "DM Sans",
    category: "Humanist sans",
    traits: "friendly, clear, approachable, balanced",
    cssFamily: "'DM Sans', system-ui, sans-serif",
  },
  {
    id: "plus-jakarta-sans",
    label: "Plus Jakarta Sans",
    category: "Neo-grotesk sans",
    traits: "product-led, startup, polished, neutral",
    cssFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
  },
  {
    id: "work-sans",
    label: "Work Sans",
    category: "Grotesk sans",
    traits: "functional, rational, minimalist, practical",
    cssFamily: "'Work Sans', system-ui, sans-serif",
  },
  {
    id: "arial",
    label: "Arial",
    category: "Sans-serif",
    traits: "universal, classic, legible",
    cssFamily: "Arial, sans-serif",
  },
  {
    id: "georgia",
    label: "Georgia",
    category: "Serif",
    traits: "authoritative, readable, classic",
    cssFamily: "Georgia, serif",
  },
  {
    id: "verdana",
    label: "Verdana",
    category: "Sans-serif",
    traits: "clear, spacious, readable",
    cssFamily: "Verdana, sans-serif",
  },
  {
    id: "times-new-roman",
    label: "Times New Roman",
    category: "Serif",
    traits: "traditional, formal, academic",
    cssFamily: "'Times New Roman', Times, serif",
  },
  {
    id: "courier-new",
    label: "Courier New",
    category: "Monospace",
    traits: "technical, retro, distinct",
    cssFamily: "'Courier New', Courier, monospace",
  },
];

function isBrandFontId(value: unknown): value is BrandFontId {
  return typeof value === "string" && BRAND_FONT_IDS.includes(value as BrandFontId);
}

export function getBrandFontById(id: BrandFontId): BrandTypographyOption {
  return BRAND_TYPOGRAPHY_CATALOG.find((font) => font.id === id) ?? BRAND_TYPOGRAPHY_CATALOG[0];
}

export function getBrandFontFamily(id: BrandFontId): string {
  return getBrandFontById(id).cssFamily;
}

export function normalizeBrandTypography(
  input?: Partial<BrandTypographySelection> | null,
): BrandTypographySelection {
  const headingFontId = isBrandFontId(input?.headingFontId)
    ? input.headingFontId
    : DEFAULT_HEADING_FONT_ID;
  const bodyFontId = isBrandFontId(input?.bodyFontId) ? input.bodyFontId : DEFAULT_BODY_FONT_ID;
  const rationale =
    typeof input?.rationale === "string" && input.rationale.trim()
      ? input.rationale.trim()
      : undefined;

  return {
    headingFontId,
    bodyFontId,
    rationale,
  };
}
